import React, { useEffect, useState } from "react";
import { ReactSVG } from "react-svg";
import { useMeasure } from "react-use";
import { useDebounce } from "use-debounce";
import _ from "lodash";
import I18n from "i18n-js";

import api from "../api";

import Loading from "../commons/CardSlider/Loading";

import useStore from "../store";

import chevronLeft from "../assets/icons/chat/chevron-left.svg";
import chevronRight from "../assets/icons/chat/chevron-right.svg";

const MAX_SUGGESTIONS = 5;
const SUGGESTION_MIN_WIDTH_PX = 150;

const Suggestions = ({ loading, onSuggestionClick }) => {

  const { suggestions, setSuggestions } = useStore(state => state.chat);

  const [ref, { width }] = useMeasure();
  
  const [showCount, setShowCount] = useState(2);
  const [suggestionWidth, setSuggestionWidth] = useState(SUGGESTION_MIN_WIDTH_PX);

  const [initialLoading, setInitialLoading] = useState(true);
  const [initialLoadingDebounced] = useDebounce(initialLoading, 500);

  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    // (width - margins)
    const normalizedWidth = (width - 48);

    const count = Math.max(0, Math.floor(normalizedWidth / SUGGESTION_MIN_WIDTH_PX));
    setShowCount(count);

    const suggestionWidth = Math.max(SUGGESTION_MIN_WIDTH_PX, normalizedWidth / suggestions?.length);
    setSuggestionWidth(suggestionWidth);
  }, [width, suggestions]);

  useEffect(() => {
    async function fetchSuggestions() {
      setInitialLoading(true);
      const suggestions = await getSuggestionsFromApi();
      setSuggestions(_.take(suggestions, MAX_SUGGESTIONS));
      setInitialLoading(false);
    }

    fetchSuggestions();
  }, []);

  const getSuggestionsFromApi = async () => {
    const res = await api.get(`/chat/suggestions?locale=${I18n.locale}`);
    console.log(res);

    if (res.data && res.data.status === "success") {
      return res.data.data;
    }
  }

  const handleSuggestionClick = (event, suggestion) => {
    if (loading) return;

    event.preventDefault();
    onSuggestionClick && onSuggestionClick(suggestion);
  }

  const redirectToGeniusWriter = (event) => {
    event.preventDefault();
    window.location.assign(`/writer?locale=${I18n.locale}`);
  }

  const getCards = () => {
    const cards = suggestions?.map(suggestion => ({
      command: suggestion.command,
      suggestion: suggestion,
      onClick: handleSuggestionClick
    }));

    cards?.push({
      command: I18n.t("chat.suggestions.genius_writer.command"),
      suggestion: null,
      onClick: redirectToGeniusWriter
    })

    return cards;
  }

  const cards = getCards();

  const handleNext = () => {
    const index = currentIndex + 1;
    if (hasNext()) {
      setCurrentIndex(index);
    }
  };

  const handlePrev = () => {
    const index = currentIndex - 1;
    if (hasPrevious()) {
      setCurrentIndex(index);
    }
  };

  const hasPrevious = () => {
    const index = currentIndex - 1;
    return index >= 0;
  }

  const hasNext = () => {
    const index = currentIndex + 1;
    return (index + showCount) <= cards.length;
  }

  const showControls = () => showCount < cards?.length;

  return (
    <div className="suggestions-section" ref={ref}>
      {initialLoadingDebounced ? (
        <Loading count={showCount} />
      ) : (
        <div className="suggestions">
          {cards?.slice(currentIndex, currentIndex + showCount).map((card, index) => (
            <div
              key={index}
              className={`suggestion ${loading ? "disabled" : ""} ${suggestionWidth > 171 ? "wide" : ""}`}
              onClick={event => card.onClick(event, card.suggestion)}
            >
              <span>
                {card.command}
              </span>
            </div>
          ))}
          {showControls() ? (
            <div className="controls">
              <button
                className="control left"
                disabled={!hasPrevious()}
                onClick={handlePrev}
              >
                <ReactSVG className="icon" src={chevronLeft} />
              </button>
              <button
                className="control right"
                disabled={!hasNext()}
                onClick={handleNext}
              >
                <ReactSVG className="icon" src={chevronRight} />
              </button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default Suggestions;
